import { useClientTranslation } from '@bts-web/utils-lokalise';
import { css, stack } from '@bts-web/utils-style-engine';
import { TTransfersNextStepStateWithActions } from '../../../../utils/useTransfersNextStepState.client';
import {
  DestinationTagValues,
  TransfersCommonStepsIds,
  TransfersDepositStepsIds,
} from '../../../../types';
import { RedirectToAssetSelection } from '../redirects/RedirectToAssetSelection';
import { RedirectToNetworkSelection } from '../redirects/RedirectToNetworkSelection';
import { AlertBox, ButtonBase, DialogBase } from '../../../../../common';
import { TCryptoTransferTranslations } from '../../../../utils/getCryptoTransferTranslations';
import { TransferWarningHeaderBox } from '../../../Reusable/TransferWarningHeaderBox/TransferWarningHeaderBox';
import { DepositWarningInfoField } from './DepositWarningInfoField';
import { ModalComponent } from '../../../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { RegularSend } from '../../../../../common/svgIcons';

const headerIconStyles = css({
  color: 'warning.fill_primary',
  borderRadius: '29px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '64px',
  width: '64px',
  backgroundColor: 'warning.fill_secondary',
});

interface DepositWarningModalProps {
  state: TTransfersNextStepStateWithActions;
  translations: TCryptoTransferTranslations;
}

const DepositWarningModal: ModalComponent<DepositWarningModalProps> = ({
  state,
  translations,
  onClose,
}) => {
  const { t } = useClientTranslation();

  const { selectedAssetData, selectedNetworkData, changeToPageAndBindActions } =
    state ?? {};

  const onDepositClick = () => {
    onClose();

    changeToPageAndBindActions({
      pageId: TransfersDepositStepsIds.DepositWallet,
      newBackAction: {
        pageId: TransfersCommonStepsIds.AssetAndNetworkSummary,
        type: 'page',
      },
    });
  };

  if (!selectedAssetData) {
    return <RedirectToAssetSelection state={state} />;
  }

  if (!selectedNetworkData) {
    return <RedirectToNetworkSelection state={state} />;
  }

  return (
    <DialogBase
      id="deposit-warning-modal"
      size="small"
      onMobileFullScreen
      visual="secondary"
      onClose={onClose}
      title={translations.cryptoDepositNavigationBarTitle}
      showHeaderBottomDivider
      smallTitle
    >
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          gap: { base: 'extra_large_5', md: 'extra_large' },
        })}
      >
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: 'large',
            justifyContent: 'center',
            flex: '1',
            textAlign: 'start',
          })}
        >
          <TransferWarningHeaderBox
            icon={
              <RegularSend
                size={32}
                data-element="icon-send"
                className={headerIconStyles}
              />
            }
            title={translations.cryptoDepositDrawerTitle}
          />
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: 'large',
            })}
          >
            <div
              className={css({
                display: 'flex',
                flexDirection: 'column',
                gap: 'extra_small',
              })}
            >
              <p
                className={css({
                  fontSize: 'body.medium',
                  lineHeight: 'body.medium',
                  letterSpacing: 'body.medium',
                  fontWeight: 'body.medium',
                  color: 'neutrals.text_primary',
                })}
              >
                {translations.cryptoDepositDrawerSubtitle}
              </p>
              <div
                className={stack({
                  gap: 'extra_small',
                  paddingBottom: 'small',
                })}
              >
                <DepositWarningInfoField
                  label={
                    <>
                      <span>{translations.cryptoDepositUseNetwork}</span>
                      &nbsp;
                      <span>
                        {t('crypto_deposit_warning_network', {
                          networkname: selectedNetworkData?.name,
                        })}
                      </span>
                    </>
                  }
                />
                <DepositWarningInfoField
                  label={
                    <>
                      <span>{translations.cryptoDepositSendOnly}</span>
                      &nbsp;
                      <span>
                        {selectedAssetData?.name} ({selectedAssetData.symbol})
                      </span>
                    </>
                  }
                />

                {selectedNetworkData?.destinationTag && (
                  <DepositWarningInfoField
                    label={
                      <>
                        {t('crypto_deposit_warning_add_transaction', {
                          destinationTag:
                            translations[
                              selectedNetworkData?.destinationTag as DestinationTagValues
                            ].toLocaleLowerCase(),
                        })}
                      </>
                    }
                  />
                )}
              </div>
              <AlertBox variant="warning">
                {translations.cryptoDepositAddressWarning}
              </AlertBox>
            </div>
          </div>
        </div>

        <footer
          className={css({
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          })}
        >
          <div className={css({ maxWidth: '342px', width: '100%' })}>
            <ButtonBase onClick={onDepositClick} visual="primary">
              {translations.iUnderstand}
            </ButtonBase>
          </div>
        </footer>
      </div>
    </DialogBase>
  );
};

export { DepositWarningModal };
